import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Col, Form, FormGroup, Input, Label } from 'reactstrap';
import { Field, reduxForm } from 'redux-form';
import { toastr } from 'react-redux-toastr';
import { isRequired } from '../../../../utils/validators';
import FormItem from '../../../common/forms/FormItem';
import Loader from '../../../common/Loader';
import FormFooter from '../../../common/forms/FormFooter';
import CustomerInputGroup from '../../../common/forms/CustomerInputGroup';
import CabinCrewTypesSelect from '../../../common/forms/select/CabinCrewTypesSelect';
import LocationSelect from '../../../common/forms/select/LocationSelect';
import CrewStatusSelect from '../../../common/forms/select/CrewStatusSelect';
import DynamicForm from '../../../common/forms/DynamicForm';
import {
  CABIN_CREW_CONTRACT_FORM_COLUMNS,
  CABIN_CREW_DOCUMENT_FORM_COLUMNS,
} from '../../../../config/dynamicFormFields';
import FileDetailResource from '../../../common/resource/FileDetailResource';

export const CabinCrewForm = ({ handleSubmit, loading, onSubmit, imageUrl }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    if (imageUrl) {
      setPreviewImage(imageUrl);
    }
  }, [imageUrl]);

  const isValidFileType = (file) => {
    const validExtensions = ['.jpg', '.jpeg', '.png'];
    const fileName = file.name.toLowerCase();
    const maxSize = 5 * 1024 * 1024;
    return (
      validExtensions.some((ext) => fileName.endsWith(ext)) &&
      file.size <= maxSize
    );
  };

  const onChangePicture = (e) => {
    const file = e.target.files[0];
    if (file && isValidFileType(file)) {
      setSelectedFile(file);
    } else {
      toastr.error(
        'Error',
        'Sólo se permiten archivos con extensión apta para imagen y tamaño máximo de 5MB',
      );
      e.target.value = null;
      setSelectedFile(null);
    }
  };

  const submitForm = (formValues) => {
    if (selectedFile && !isValidFileType(selectedFile)) {
      toastr.error(
        'Error',
        'El archivo seleccionado no es un archivo de imagen válido o excede el tamaño máximo permitido',
      );
      return;
    }

    const newFormValues = {
      ...formValues,
      file: selectedFile,
    };

    onSubmit(newFormValues);
  };

  if (loading) return <Loader />;

  return (
    <Form onSubmit={handleSubmit(submitForm)}>
      <CustomerInputGroup
        label="Cliente"
        labelRequired
        name="customer"
        form="CabinCrewForm"
        validate={[isRequired]}
        showDetails
      />
      <h3>Documentos</h3>
      <DynamicForm
        name="cabinCrewDocuments"
        columns={CABIN_CREW_DOCUMENT_FORM_COLUMNS}
      />
      <h3>Estado</h3>
      <FormGroup row>
        <FormItem label="Tipo" required>
          <Field
            name="cabinCrewType"
            component={CabinCrewTypesSelect}
            placeholder="Tipo"
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>
      <FormGroup row>
        <FormItem label="Base" required>
          <Field
            name="homeBase"
            component={LocationSelect}
            placeholder="Base"
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>
      <FormGroup row>
        <FormItem label="Estado" required>
          <Field
            name="crewStatus"
            component={CrewStatusSelect}
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>
      <h3>Contratos</h3>
      <DynamicForm
        name="cabinCrewContracts"
        columns={CABIN_CREW_CONTRACT_FORM_COLUMNS}
      />
      <FormGroup row>
        <Col sm={3} xs={12}>
          <Label>Foto</Label>
        </Col>
        <Col sm={9} xs={12}>
          <Input
            name="voucher"
            type="file"
            accept={['.jpg', '.jpeg', '.png']}
            onChange={onChangePicture}
            className="mb-4"
          />
          {selectedFile && <FileDetailResource file={selectedFile} />}

          {previewImage && !selectedFile && (
            <img
              src={previewImage}
              className="img-fluid"
              aria-hidden="true"
              width={300}
              alt=""
            />
          )}
        </Col>
      </FormGroup>
      <FormFooter />
    </Form>
  );
};

CabinCrewForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  imageUrl: PropTypes.string,
};

CabinCrewForm.defaultProps = {
  imageUrl: null,
};

const mapStateToProps = ({ TrafficUnit }) => ({
  loading: !TrafficUnit.CabinCrew.getIn(['current', 'activity']).isEmpty(),
});

const formComponent = reduxForm({
  form: 'CabinCrewForm',
})(CabinCrewForm);

export default connect(mapStateToProps)(formComponent);
